import type { NextRouter } from 'next/router';
import { ROUTES } from '@/utils/pathRoutes';
import { getOrganizationIndexedDB } from '@/utils/organizationIndexedDBUtils';
import { isVanHoaPath } from '@/utils/vanHoaHuongDan';

const withTrailingSlash = (route: string) => (route.endsWith('/') ? route : `${route}/`);

export const normalizeVanHoaPrefetchPath = (path: string) =>
  path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

/** Ưu tiên prefetch trước (menu chính). */
const VAN_HOA_PRIORITY_ROUTES: string[] = [
  ROUTES.VAN_HOA.DASHBOARD,
  ROUTES.VAN_HOA.NGOI_NHA,
  ROUTES.VAN_HOA.HANH_VI_THEN_CHOT,
  ROUTES.VAN_HOA.AN_LE,
  '/van-hoa/danh-gia',
  '/van-hoa/bang-ke-hoach',
  '/van-hoa/du-an',
  '/van-hoa/timeline',
];

const VAN_HOA_PREFETCH_ROUTES: string[] = [
  ...VAN_HOA_PRIORITY_ROUTES,
  ROUTES.VAN_HOA.CAU_HOI_AN_LE,
  ROUTES.VAN_HOA.KS_VH_HIEN_TAI_TUONG_LAI,
  ROUTES.VAN_HOA.BAO_CAO_HIEN_TAI_TUONG_LAI(),
  ROUTES.VAN_HOA.XUONG_CA_KE_HOACH,
  '/van-hoa/dot-on-tap',
  '/van-hoa/dot-danh-gia/dot-khao-sat-truong-thanh-van-hoa',
  '/van-hoa/dot-danh-gia/dot-khao-sat-van-hoa',
  '/van-hoa/tieu-chi-nhan-vien/trai-nghiem-gan-ket-diem-cham',
  '/van-hoa/to-chuc/bo-phan',
  '/van-hoa/to-chuc/nhan-vien',
  '/van-hoa/cau-hinh',
];

const prefetchedRoutes = new Set<string>();
const inflightRoutes = new Set<string>();
let batchStarted = false;
let orgCacheWarmed = false;

const STAGGER_MS = 25;
const PARALLEL = 3;

function runWhenIdle(task: () => void) {
  if (typeof window === 'undefined') return;
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (ric) {
    ric(task, { timeout: 1200 });
    return;
  }
  window.setTimeout(task, 200);
}

/** Prefetch một route VH ngay (hover menu / sắp chuyển trang). */
export function prefetchVanHoaRouteNow(router: NextRouter, href: string) {
  if (typeof window === 'undefined' || !href) return;
  const pathOnly = href.split('?')[0].split('#')[0];
  if (!isVanHoaPath(pathOnly)) return;

  const route = withTrailingSlash(pathOnly);
  const key = normalizeVanHoaPrefetchPath(route);
  if (prefetchedRoutes.has(key) || inflightRoutes.has(key)) return;

  inflightRoutes.add(key);
  void router.prefetch(route).finally(() => {
    inflightRoutes.delete(key);
    prefetchedRoutes.add(key);
  });
}

function prefetchRoutes(router: NextRouter, routes: string[], current: string) {
  const pending = routes
    .map(withTrailingSlash)
    .filter((route) => {
      const key = normalizeVanHoaPrefetchPath(route);
      return key !== current && !prefetchedRoutes.has(key) && !inflightRoutes.has(key);
    });

  if (!pending.length) return;

  let index = 0;

  const prefetchNext = () => {
    const batch: string[] = [];
    while (batch.length < PARALLEL && index < pending.length) {
      batch.push(pending[index]);
      index += 1;
    }
    if (!batch.length) return;

    batch.forEach((route) => {
      const key = normalizeVanHoaPrefetchPath(route);
      inflightRoutes.add(key);
      void router.prefetch(route).finally(() => {
        inflightRoutes.delete(key);
        prefetchedRoutes.add(key);
      });
    });

    window.setTimeout(prefetchNext, STAGGER_MS);
  };

  prefetchNext();
}

/** Prefetch chunk các route VH — ưu tiên menu chính trước. */
export function scheduleVanHoaRoutePrefetch(router: NextRouter, currentPath?: string) {
  if (typeof window === 'undefined') return;

  const current = normalizeVanHoaPrefetchPath(currentPath ?? router.asPath);

  prefetchRoutes(router, VAN_HOA_PRIORITY_ROUTES, current);

  if (batchStarted) return;
  batchStarted = true;

  runWhenIdle(() => {
    const rest = VAN_HOA_PREFETCH_ROUTES.filter((r) => !VAN_HOA_PRIORITY_ROUTES.includes(r));
    prefetchRoutes(router, rest, current);
  });
}

export function warmVanHoaOrgCache() {
  if (typeof window === 'undefined' || orgCacheWarmed) return;
  orgCacheWarmed = true;
  void getOrganizationIndexedDB();
}
